/**
 * Godot 4.7.2's area light atlas: every `area_texture` in one RGBA8 texture, each with a border and
 * a chain of blurred mips, and where each light samples it (`texture_storage.cpp:3345-3510`,
 * `light_storage.cpp:1132-1141`). The shader picks a mip by the fragment's distance to the light.
 */
/*
 * The layout is derived from Godot Engine
 * (`servers/rendering/renderer_rd/storage_rd/texture_storage.cpp`), used under the MIT licence:
 *
 *   Copyright (c) 2014-present Godot Engine contributors (see AUTHORS.md).
 *   Copyright (c) 2007-2014 Juan Linietsky, Ariel Manzur.
 *
 * See THIRD-PARTY-NOTICES.md for the full notice.
 */
import { nextPowerOfTwo } from '../../godot/math/powerOfTwo.js';
import { fractionOfAtlas } from '../textureAtlas/atlasFraction.js';
import { packCells } from '../textureAtlas/atlasPacking.js';
import { areaTextureMipmaps } from './atlasMipmaps.js';
import { AREA_LIGHT_ATLAS_MIPMAPS } from '../../godot/rendering/lights/areaLightAtlas.js';
/** Each texture takes whole cells of this side, plus one cell of border (`texture_storage.cpp:3361`). */
const BORDER = 1 << (AREA_LIGHT_ATLAS_MIPMAPS - 1);
/** A texture's cells, and the texels it fills: whole cells, or a power of two below one cell (`:3373-3383`). */
function cellOf(image) {
    const cellsX = Math.ceil(image.width / BORDER);
    const cellsY = Math.ceil(image.height / BORDER);
    return {
        width: cellsX + 1,
        height: cellsY + 1,
        pixelWidth: image.width < BORDER ? nextPowerOfTwo(image.width) : cellsX * BORDER,
        pixelHeight: image.height < BORDER ? nextPowerOfTwo(image.height) : cellsY * BORDER,
    };
}
/** Godot's `cone_angle` for an area light: the mips the texture's shorter side holds, up to the atlas's. */
function maxMipmapOf(texture) {
    const shorter = Math.max(Math.min(texture.width, texture.height), 1);
    return Math.min(Math.floor(Math.log2(shorter)), AREA_LIGHT_ATLAS_MIPMAPS) - 1;
}
/**
 * The atlas of `images`, in the order Godot adds them. With no image, the atlas is one blank cell
 * that still holds every mip (`:3460-3463`).
 */
export function buildAreaLightAtlas(images) {
    const cells = images.map(cellOf);
    const { width, height, positions } = packCells(cells, 1, BORDER);
    const placed = cells.map((cell, index) => ({
        image: images[index],
        x: positions[index].x * BORDER,
        y: positions[index].y * BORDER,
        width: cell.pixelWidth,
        height: cell.pixelHeight,
    }));
    const placements = placed.map((texture) => ({
        rect: fractionOfAtlas(texture, { width, height }),
        maxMipmap: maxMipmapOf(texture),
    }));
    const mipmaps = areaTextureMipmaps(width, height, placed);
    return { width, height, mipmaps, placements };
}
//# sourceMappingURL=atlasLayout.js.map